import { createContext, forwardRef, useContext } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useIsPhone } from "@/lib/useMediaQuery";
import { cn } from "@/lib/utils";

/**
 * One form, two presentations: a centred dialog on a pointer device, a
 * full-screen page on a phone.
 *
 * It is the same component tree either way, so callers write a form once. A
 * centred dialog on a phone puts its controls in the middle of the screen and
 * slides its footer under the keyboard. A bottom sheet did no better once the
 * keyboard was up: sized by its content and pinned to the bottom, it was
 * squeezed into the strip above the keyboard, leaving a couple of fields to
 * scroll through. Taking the whole screen gives the form everything the
 * keyboard leaves free.
 *
 * The choice is shared through context rather than recomputed per part, so a
 * viewport that changes mid-interaction — a rotation, a desktop window being
 * dragged narrower — cannot leave a phone body inside a dialog shell.
 */
const PresentationContext = createContext(false);

export function ResponsiveDialog({ children, ...props }) {
  const phone = useIsPhone();
  return (
    <PresentationContext.Provider value={phone}>
      <Dialog {...props}>{children}</Dialog>
    </PresentationContext.Provider>
  );
}

export const ResponsiveDialogContent = forwardRef(function ResponsiveDialogContent(
  { className, children, ...props },
  ref,
) {
  const phone = useContext(PresentationContext);

  if (!phone) {
    return <DialogContent ref={ref} className={className} {...props}>{children}</DialogContent>;
  }

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Content
        ref={ref}
        // The page is its own scroller, with the header and footer sticky at
        // either end, so the title and the primary action stay put while the
        // fields between them scroll. `mt-auto` on the footer holds it to the
        // bottom when the form is shorter than the screen.
        //
        // `inset-0` is enough to clear the keyboard: the viewport meta asks
        // the browser to resize the layout viewport for it, so the page's
        // bottom edge lands on the keyboard's top edge with no measuring.
        //
        // The horizontal padding belongs to the scrolling element itself, so
        // the scrollbar rides in a gutter beside the content instead of taking
        // 8px out of it and cropping full-width inputs.
        className={cn(
          "fixed inset-0 z-50 flex flex-col overflow-y-auto overscroll-contain bg-card px-4 [scrollbar-gutter:stable]",
          "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom-8 data-[state=open]:slide-in-from-bottom-8",
          className,
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});

export function ResponsiveDialogHeader({ className, children, ...props }) {
  const phone = useContext(PresentationContext);
  if (!phone) return <DialogHeader className={className} {...props}>{children}</DialogHeader>;

  // With no backdrop to tap and no sheet to drag down, the page needs a close
  // control of its own, and the top corner is where a full-screen page keeps it.
  return (
    <div
      className={cn(
        "sticky top-0 z-10 -mx-4 mb-4 flex items-start gap-3 border-b border-border bg-card px-4 pb-3 pt-[max(1rem,env(safe-area-inset-top))]",
        className,
      )}
      {...props}
    >
      <div className="flex min-w-0 flex-1 flex-col space-y-1.5 text-left">{children}</div>
      <DialogPrimitive.Close
        className="-m-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-testid="responsive-dialog-close"
      >
        <X className="h-5 w-5" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </div>
  );
}

export const ResponsiveDialogTitle = DialogTitle;

export function ResponsiveDialogDescription(props) {
  const phone = useContext(PresentationContext);
  return (
    <DialogDescription
      {...props}
      // Once the keyboard has taken most of the screen, two lines restating
      // what the form obviously does are the first thing that should go. It
      // stays in the accessibility tree either way.
      className={cn(phone && "[@media(max-height:520px)]:sr-only", props.className)}
    />
  );
}

export function ResponsiveDialogFooter(props) {
  const phone = useContext(PresentationContext);
  return (
    <DialogFooter
      {...props}
      // Sticky on a phone so the primary action stays reachable however far
      // the form is scrolled. It sits inside the scroller, so it needs its own
      // background to sit over the content passing behind it. Side by side
      // rather than stacked, so the buttons take one row of what the keyboard
      // leaves. A short viewport means the keyboard is up and covering the
      // home indicator, so the safe-area padding is dropped there.
      className={cn(
        phone && "sticky bottom-0 z-10 -mx-4 mt-auto flex-row gap-2 border-t border-border bg-card px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] [@media(max-height:520px)]:pb-3 [&>*]:flex-1",
        props.className,
      )}
    />
  );
}
